import { Flex, Stack, useToast } from "@chakra-ui/react";
import { UseFieldArrayAppend, UseFieldArrayUpdate, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ReminderTriggerForm from "features/Trigger/ReminderTrigger/Form";
import { useCallback } from "react";
import { PrimaryButton, SecondaryButton } from "components/Button";
import { ReminderSchema, reminderSchema } from "../schema";

// The reminder form binds to `data.offsetValue` + `data.offsetDirection`, but
// the API stores a single signed `offset`. Convert in both directions so the
// form can display existing reminders correctly AND submit a payload the
// backend's TaskReminderTiming jsonb_accessor understands.
function toLocalReminderTrigger(api: any): any {
  if (!api) return api;
  const offset = api?.data?.offset ?? 0;
  let offsetDirection: "before" | "after" | "exact" = "exact";
  if (offset > 0) offsetDirection = "after";
  else if (offset < 0) offsetDirection = "before";
  return {
    ...api,
    data: {
      referenceEvent: api?.data?.referenceEvent,
      offsetUnit: api?.data?.offsetUnit,
      offsetValue: Math.abs(offset),
      offsetDirection,
    },
  };
}

function toApiReminderTrigger(local: any): any {
  if (!local) return local;
  const direction = local?.data?.offsetDirection;
  const value = local?.data?.offsetValue ?? 0;
  let offset = 0;
  if (direction === "after") offset = value;
  else if (direction === "before") offset = -value;
  return {
    ...local,
    data: {
      referenceEvent: local?.data?.referenceEvent,
      offset,
      offsetUnit: offset === 0 ? null : local?.data?.offsetUnit,
    },
  };
}

export default function TemplateReminderForm({
  value,
  index,
  update,
  append,
  onClose,
  isNew = false,
}: {
  value: any;
  update: UseFieldArrayUpdate<any>;
  append: UseFieldArrayAppend<any>;
  index: number;
  isNew?: boolean;
  onClose?: () => void;
}) {
  const { remindAtTrigger = undefined, ...restValue } = value;
  const toast = useToast();
  const form = useForm({
    mode: "onBlur",
    resolver: yupResolver(reminderSchema),
    defaultValues: {
      remindAtTrigger: toLocalReminderTrigger({
        ...remindAtTrigger,
        type: "Triggerable::TaskReminderTiming",
      }),
      ...restValue,
    },
  });

  const {
    handleSubmit,
    reset,
  } = form;

  const appendToReminders = useCallback(
    (data: ReminderSchema) => {
      // Convert the form's local trigger shape (offsetValue + offsetDirection)
      // back to the API shape (signed offset) before storing in the field array.
      const apiData = { ...data, remindAtTrigger: toApiReminderTrigger((data as any).remindAtTrigger) };
      if (isNew) {
        append(apiData);
      } else {
        update(index, { ...apiData, fieldId: value.fieldId });
      }
      reset(data, { keepDefaultValues: false });
      onClose?.();
    },
    [append, index, isNew, onClose, reset, update, value.fieldId],
  );

  const onCloseModal = useCallback(() => {
    reset();
    onClose?.();
  }, [onClose, reset]);

  return (
    <Stack>
      <ReminderTriggerForm form={form} fieldName="remindAtTrigger" />
      <Flex w="100%" gap="2" justifySelf="flex-end">
        <SecondaryButton onClick={onCloseModal}>Cancel</SecondaryButton>
        <PrimaryButton
          onClick={handleSubmit(appendToReminders, (_errors) => {
            console.log(_errors);
            toast({
              title: "Please fix the errors before continuing.",
              status: "error",
              duration: 5000,
            });
          })}
        >
          {isNew ? "Add" : "Update"}
        </PrimaryButton>
      </Flex>
    </Stack>
  );
}
